import { shortcut } from './responsive';
import { Notification, type NoticeInput } from './notifications';
import { useId, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { hexA } from '@scribbleplot/shared';
import { color, font, radius, shadow } from '../tokens';

/**
 * Small shared building blocks.
 *
 * Shared classes own interaction states; canvas geometry and dynamic accent
 * colours stay inline. Legacy style overrides remain supported during migration.
 */

type ButtonProps = {
  children: ReactNode;
  variant?: 'default' | 'primary' | 'danger' | 'quiet';
  loading?: boolean;
  className?: string;
  onClick?: () => void;
  title?: string;
  disabled?: boolean;
  style?: CSSProperties;
  hoverStyle?: CSSProperties;
  type?: 'button' | 'submit';
  ariaLabel?: string;
  ariaPressed?: boolean;
  /** Exposes the DOM node so callers can restore focus to it. */
  buttonRef?: RefObject<HTMLButtonElement | null>;
};

export function Button({
  children,
  variant = 'default',
  loading = false,
  className = '',
  onClick,
  title,
  disabled,
  style,
  hoverStyle,
  type = 'button',
  ariaLabel,
  ariaPressed,
  buttonRef,
}: ButtonProps) {
  const [hover, setHover] = useState(false);
  return (
    <button
      ref={buttonRef}
      className={`sp-control sp-control-${variant} ${className}`}
      aria-busy={loading || undefined}
      data-tooltip={title ? shortcut(title) : undefined}
      type={type}
      title={title ? shortcut(title) : undefined}
      aria-label={ariaLabel ?? (title ? shortcut(title) : undefined)}
      aria-pressed={ariaPressed}
      disabled={disabled || loading}
      onClick={onClick}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      style={{
        fontFamily: 'inherit',
        cursor: disabled || loading ? 'default' : 'pointer',
        opacity: disabled || loading ? 0.55 : 1,
        transition: 'background .12s, filter .12s, color .12s, border-color .12s',
        ...style,
        ...(hover && !disabled ? hoverStyle : null),
      }}
    >
      {children}
    </button>
  );
}

/** Primary call to action, in the user's accent colour. */
export function AccentButton({
  children,
  onClick,
  accent,
  size = 'md',
  disabled,
  type = 'button',
  style,
  glow,
  buttonRef,
}: {
  children: ReactNode;
  onClick?: () => void;
  accent: string;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  type?: 'button' | 'submit';
  style?: CSSProperties;
  glow?: boolean;
  buttonRef?: RefObject<HTMLButtonElement | null>;
}) {
  const dims =
    size === 'lg'
      ? { height: 40, padding: '0 22px', fontSize: 14, borderRadius: 11 }
      : size === 'sm'
        ? { height: 30, padding: '0 14px', fontSize: 14, borderRadius: radius.md }
        : { height: 32, padding: '0 16px', fontSize: 14, borderRadius: radius.md };
  return (
    <Button
      type={type}
      onClick={onClick}
      disabled={disabled}
      buttonRef={buttonRef}
      hoverStyle={{ filter: 'brightness(1.12)' }}
      style={{
        ...dims,
        border: 'none',
        background: accent,
        color: color.onAccent,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        ...(glow ? { boxShadow: `0 0 26px ${hexA(accent, 0.35)}` } : null),
        ...style,
      }}
    >
      {children}
    </Button>
  );
}

export function GhostButton({
  children,
  onClick,
  style,
  size = 'md',
}: {
  children: ReactNode;
  onClick?: () => void;
  style?: CSSProperties;
  size?: 'md' | 'lg';
}) {
  return (
    <Button
      onClick={onClick}
      hoverStyle={{ background: color.hover }}
      style={{
        height: size === 'lg' ? 40 : 32,
        padding: size === 'lg' ? '0 17px' : '0 13px',
        borderRadius: size === 'lg' ? 11 : radius.md,
        border: `1px solid rgba(255,255,255,.18)`,
        background: 'transparent',
        color: color.text,
        fontSize: size === 'lg' ? 13.5 : 13,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </Button>
  );
}

/** Square icon button used across the top bar and tool rail. */
export function IconButton({
  children,
  onClick,
  title,
  active,
  accent,
  size = 32,
  style,
}: {
  children: ReactNode;
  onClick?: () => void;
  title?: string;
  active?: boolean;
  accent: string;
  size?: number;
  style?: CSSProperties;
}) {
  return (
    <Button
      onClick={onClick}
      title={title}
      ariaLabel={title}
      ariaPressed={active}
      hoverStyle={{ background: color.hoverStrong }}
      style={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: size >= 36 ? 10 : radius.md,
        border: 'none',
        background: active ? hexA(accent, 0.15) : 'transparent',
        color: active ? accent : color.icon,
        flex: 'none',
        ...style,
      }}
    >
      {children}
    </Button>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
  autoFocus,
  onKeyDown,
  style,
  ariaLabel,
  name,
  autoComplete,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  autoFocus?: boolean;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  style?: CSSProperties;
  ariaLabel?: string;
  name?: string;
  autoComplete?: string;
}) {
  const id = useId();
  const label =
    ariaLabel ??
    (type === 'email'
      ? 'Email address'
      : type === 'password'
        ? placeholder?.startsWith('Repeat')
          ? 'Repeat password'
          : placeholder?.startsWith('Current')
            ? 'Current password'
            : placeholder?.startsWith('New')
              ? 'New password'
              : 'Password'
        : (placeholder ?? name ?? 'Text'));
  const [focus, setFocus] = useState(false);
  return (
    <label className="sp-field" htmlFor={id}>
      <span>{label}</span>
      <input
        id={id}
        className="sp-input"
        name={name}
        type={type}
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        aria-label={ariaLabel ?? placeholder}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{
          height: 36,
          background: color.input,
          border: `1px solid ${focus ? 'rgba(167,139,250,.6)' : color.lineStrong}`,
          borderRadius: radius.md,
          color: color.text,
          fontSize: 13,
          fontFamily: 'inherit',
          padding: '0 11px',
          outline: 'none',
          width: '100%',
          boxSizing: 'border-box',
          ...style,
        }}
      />
    </label>
  );
}

/** Section label in the small monospace caps used throughout the design. */
export function Eyebrow({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        fontFamily: font.mono,
        fontSize: 10,
        letterSpacing: '.12em',
        color: color.textFaint,
        textTransform: 'uppercase',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Toggle({
  on,
  onChange,
  accent,
  label,
}: {
  on: boolean;
  onChange: (on: boolean) => void;
  accent: string;
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      style={{
        width: 38,
        height: 22,
        borderRadius: 11,
        border: 'none',
        background: on ? accent : '#33353D',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background .15s',
        padding: 0,
        flex: 'none',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          left: on ? 19 : 3,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left .15s',
        }}
      />
    </button>
  );
}

export function Avatar({
  initials,
  accent,
  size = 30,
  onClick,
  title,
}: {
  initials: string;
  accent: string;
  size?: number;
  onClick?: () => void;
  title?: string;
}) {
  const content = (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: hexA(accent, 0.2),
        color: accent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size > 40 ? 21 : 12,
        fontWeight: 700,
        flex: 'none',
      }}
    >
      {initials}
    </span>
  );
  if (!onClick) return content;
  return (
    <Button
      onClick={onClick}
      title={title}
      hoverStyle={{ filter: 'brightness(1.2)' }}
      style={{ border: 'none', background: 'transparent', padding: 0, display: 'flex' }}
    >
      {content}
    </Button>
  );
}

export function Toast({ message }: { message: NoticeInput | null }) {
  return <Notification message={message} />;
}

export function Spinner({ accent, label = 'Loading' }: { accent: string; label?: string }) {
  return (
    <div
      role="status"
      aria-label={label}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}
    >
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          border: `2px solid ${hexA(accent, 0.25)}`,
          borderTopColor: accent,
          animation: 'sp-spin .7s linear infinite',
        }}
      />
    </div>
  );
}
